import { HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { LoadingService } from '../services/loading.service';

export const LoadingInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<any> => {
  const loadingService = inject(LoadingService);
  
  // Skip loading for certain requests (like authentication checks, file uploads, etc.)
  const skipLoadingUrls = [
    '/auth/verify-token',
    '/health',
    '/ping',
    'assets/',
    '.json',
    '.svg',
    '.png',
    '.jpg',
    '.jpeg',
    '.gif',
    '.ico'
  ];

  const shouldSkipLoading = skipLoadingUrls.some(url => 
    req.url.toLowerCase().includes(url.toLowerCase())
  );

  // Check if this is a background request (has custom header)
  const isBackgroundRequest = req.headers.has('X-Skip-Loading');

  if (shouldSkipLoading || isBackgroundRequest) {
    return next(req);
  }

  // Generate unique request identifier
  const requestId = generateRequestId(req);
  
  // Determine loading message based on HTTP method
  let loadingMessage = 'Loading...';
  switch (req.method.toUpperCase()) {
    case 'POST':
      loadingMessage = 'Saving...';
      break;
    case 'PUT':
    case 'PATCH':
      loadingMessage = 'Updating...';
      break;
    case 'DELETE':
      loadingMessage = 'Deleting...';
      break;
    case 'GET':
    default:
      loadingMessage = 'Loading...';
      break;
  }

  // Show loading
  loadingService.showForRequest(requestId, loadingMessage, 'spinner');

  return next(req).pipe(
    tap((event) => {
      // Optional: You can add progress tracking here for file uploads
      if (event instanceof HttpResponse) {
        // Request completed successfully
      }
    }),
    finalize(() => {
      // Hide loading when request completes (success or error)
      loadingService.hideForRequest(requestId);
    })
  );
};

/**
 * Generate a unique identifier for the request
 */
function generateRequestId(req: HttpRequest<unknown>): string {
  const url = req.url.split('?')[0]; // Remove query parameters
  const method = req.method;
  const timestamp = Date.now();
  return `${method}-${url}-${timestamp}`;
}